import { useCallback } from 'react';
import { type QueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import { usePaginationSearchParams } from '#packages/web/hooks/use-pagination';
import { routes } from '#apps/client-web/app/routes';

import { useAuditListQuery } from '#apps/client-web/features/audit/hooks/use-audit-list-query';
import { type AuditService } from '#apps/client-web/features/audit/services/audit';

const DEFAULT_PAGE_SIZE = 1000;

export type AuditListPageViewModel = ReturnType<
  typeof useAuditListPageViewModel
>;

type AuditListPageViewModelProps = LoggingContext & {
  auditService: AuditService;
  queryClient: QueryClient;
};

export const useAuditListPageViewModel = ({
  logger,
  auditService,
  queryClient,
}: AuditListPageViewModelProps) => {
  const navigate = useNavigate();

  const pagination = usePaginationSearchParams({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  logger?.debug(
    {
      pagination: {
        pageNumber: pagination.currentPageNumber,
        pageSize: pagination.currentPageSize,
      },
    },
    'AuditListPageViewModel pagination',
  );

  const auditListQuery = useAuditListQuery({
    auditService,
    queryClient,
    collectionName: 'active',
    pagination: {
      pageNumber: pagination.currentPageNumber,
      pageSize: pagination.currentPageSize,
    },
  });

  const onCreate = useCallback(() => {
    logger?.info('Navigating to create audit page');
    navigate(routes.audit.create);
  }, [navigate]);

  return {
    pagination,
    auditListQuery,

    onCreate,
  };
};
